import { useRef, useState } from 'react';
import { Mic, Square, Play, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AudioRecorder({ onRecorded }) {
  const [recording, setRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioB64, setAudioB64] = useState(null);
  const [mime, setMime] = useState(null);
  const [duration, setDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startTimeRef = useRef(0);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        setMime(mr.mimeType || 'audio/webm');
        // Convert to base64
        const buf = await blob.arrayBuffer();
        const bytes = new Uint8Array(buf);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        const b64 = btoa(binary);
        setAudioB64(b64);
        onRecorded?.({ base64: b64, mime: mr.mimeType || 'audio/webm' });
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      startTimeRef.current = Date.now();
      timerRef.current = setInterval(() => setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000)), 300);
    } catch (e) {
      toast.error("Impossible d'accéder au micro. Autorise le micro dans ton navigateur.");
      console.error(e);
    }
  };

  const stop = () => {
    mediaRecorderRef.current?.stop();
    clearInterval(timerRef.current);
    setRecording(false);
  };

  const remove = () => {
    setAudioUrl(null);
    setAudioB64(null);
    setDuration(0);
    onRecorded?.(null);
  };

  return (
    <div className="mt-3 flex items-center gap-3 flex-wrap">
      {!recording && !audioUrl && (
        <button data-testid="record-start" onClick={start} className="btn-ghost inline-flex items-center gap-2">
          <Mic size={16} className="text-orange-400" /> Enregistrer une reprise
        </button>
      )}
      {recording && (
        <button data-testid="record-stop" onClick={stop} className="btn-primary inline-flex items-center gap-2">
          <Square size={14} /> Stop · {duration}s
        </button>
      )}
      {audioUrl && !recording && (
        <div className="flex items-center gap-3 card-stone px-3 py-2">
          <Play size={14} className="text-orange-400" />
          <audio data-testid="recorded-audio" src={audioUrl} controls className="h-8" />
          <button data-testid="record-delete" onClick={remove} className="text-stone-400 hover:text-red-400"><Trash2 size={14} /></button>
        </div>
      )}
    </div>
  );
}
